// Product option modal — Normalisers: raw item payload (variants, modifiers, combos, upsells,
// ingredients, service items) -> the option groups the modal renders.
import {
  isServiceItemSoldOut,
  resolveModifierOptionPrice,
  resolveServiceItemPrice,
} from './pricing';

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

export function normalizeId(value, fallback) {
  return String(value ?? fallback ?? '');
}

export function normalizeLabel(option, fallback = 'Option') {
  return (
    option?.name ??
    option?.label ??
    option?.variant_name ??
    option?.modifier_name ??
    option?.item_name ??
    option?.ingredient_name ??
    option?.value ??
    fallback
  );
}

export function normalizeVariant(option, index) {
  const hasOwnServiceItems =
    getVariantServiceItemList(option).length > 0 ||
    getVariantServiceItemGroupList(option).length > 0;

  return {
    ...option,
    id: normalizeId(option?.variant_id ?? option?.id ?? option?.value, `variant-${index}`),
    variant_id: option?.variant_id ?? option?.id,
    name: normalizeLabel(option, `Variant ${index + 1}`),
    description: option?.description ?? option?.subtitle ?? option?.subtext ?? '',
    priceDelta: firstNumber(option?.price_delta, option?.priceDelta, option?.delta_price, 0) ?? 0,
    isDefault: Boolean(option?.is_default ?? option?.default ?? option?.selected),
    extra_upsell_group: option?.extra_upsell_group,
    __hasOwnServiceItems: hasOwnServiceItems,
  };
}

export function getServiceItemDetails(item) {
  return item?.service_item_details ?? item?.serviceItemDetails ?? item?.details ?? item;
}

export function getVariantServiceItemList(option) {
  const snakeCaseItems = asArray(option?.service_items);
  if (snakeCaseItems.length > 0) return snakeCaseItems;
  return asArray(option?.serviceItems);
}

export function getVariantServiceItemGroupList(option) {
  const snakeCaseGroups = asArray(option?.service_item_groups);
  if (snakeCaseGroups.length > 0) return snakeCaseGroups;
  return asArray(option?.serviceItemGroups);
}

export function getGroupServiceItemList(group) {
  const snakeCaseItems = asArray(group?.service_items);
  if (snakeCaseItems.length > 0) return snakeCaseItems;
  return asArray(group?.serviceItems);
}

export function normalizeServiceItem(item, index, opts = {}) {
  const details = getServiceItemDetails(item);
  const rawGroupId =
    item?.service_item_group_id ??
    item?.serviceItemGroupId ??
    details?.service_item_group_id ??
    details?.serviceItemGroupId;
  const serviceItemGroupId = Array.isArray(rawGroupId) ? rawGroupId[0] : rawGroupId;
  const id = normalizeId(
    item?.service_item_id ?? item?.serviceItemId ?? details?.service_item_id ?? details?.id,
    `service-item-${index}`,
  );

  return {
    ...item,
    id,
    service_item_id: item?.service_item_id ?? item?.serviceItemId ?? details?.service_item_id ?? id,
    service_item_group_id: serviceItemGroupId,
    name:
      item?.name ??
      item?.service_item_name ??
      details?.name ??
      details?.service_item_name ??
      `Service ${index + 1}`,
    priceDelta: resolveServiceItemPrice(item, opts),
    isMandatory: Boolean(item?.is_mandatory ?? item?.isMandatory ?? details?.is_mandatory),
    isSoldOut: isServiceItemSoldOut(item, opts),
    details,
  };
}

export function getVariantServiceItems(option, opts = {}) {
  return getVariantServiceItemList(option).map((item, index) =>
    normalizeServiceItem(item, index, opts),
  );
}

export function normalizeServiceItemGroup(group, index, opts = {}) {
  const details = group?.service_item_group_details ?? group?.serviceItemGroupDetails ?? group;
  const id = normalizeId(
    group?.service_item_group_id ??
      group?.serviceItemGroupId ??
      details?.service_item_group_id ??
      details?.id,
    `service-item-group-${index}`,
  );

  return {
    ...group,
    id,
    service_item_group_id:
      group?.service_item_group_id ??
      group?.serviceItemGroupId ??
      details?.service_item_group_id ??
      id,
    name: group?.name ?? details?.name ?? `Service group ${index + 1}`,
    service_items: getGroupServiceItemList(group).map((item, itemIndex) => ({
      ...normalizeServiceItem(item, itemIndex, opts),
      service_item_group_id:
        group?.service_item_group_id ??
        group?.serviceItemGroupId ??
        details?.service_item_group_id ??
        id,
    })),
  };
}

export function getVariantServiceItemGroups(option, opts = {}) {
  return getVariantServiceItemGroupList(option).map((group, index) =>
    normalizeServiceItemGroup(group, index, opts),
  );
}

export function hasVariantServiceItems(option) {
  return (
    getVariantServiceItemList(option).length > 0 ||
    getVariantServiceItemGroupList(option).length > 0
  );
}

export function variantDeclaresServiceItems(option) {
  if (!option) return false;
  if (option.__hasOwnServiceItems !== undefined) return Boolean(option.__hasOwnServiceItems);
  return hasVariantServiceItems(option);
}

export function variantServiceLookupKeys(groupId, option) {
  const keys = [];
  for (const value of [
    option?.variant_id,
    option?.id,
    option?.value,
    option?.name,
    option?.variant_name,
  ]) {
    if (value !== undefined && value !== null && value !== '') {
      keys.push(`${groupId || ''}::${String(value)}`);
    }
  }
  return keys;
}

export function firstVariantServiceMatch(map, groupId, option) {
  for (const key of variantServiceLookupKeys(groupId, option)) {
    const match = map.get(key);
    if (match) return match;
  }
  return null;
}

export function getVariantServiceItemEntries(option, opts = {}) {
  const groups = getVariantServiceItemGroups(option, opts);
  const directEntries = getVariantServiceItems(option, opts).map((item) => ({
    item,
    serviceItemGroupId: '',
  }));
  const groupedEntries = groups.flatMap((group) =>
    group.service_items.map((item) => ({
      item,
      serviceItemGroupId: group.service_item_group_id,
      groupName: group.name,
    })),
  );

  return [...directEntries, ...groupedEntries];
}

export function normalizeVariantGroup(group, index) {
  const options = asArray(group?.variants ?? group?.items ?? group?.options ?? group).map(
    (opt, optIdx) => normalizeVariant(opt, optIdx),
  );

  return {
    ...group,
    id: normalizeId(group?.variant_group_id ?? group?.group_id ?? group?.id, `group-${index}`),
    variant_group_id: group?.variant_group_id ?? group?.group_id ?? group?.id,
    name: group?.name ?? group?.label ?? `Variant Group ${index + 1}`,
    required: Boolean(group?.is_required ?? group?.required ?? group?.min_selection > 0),
    options,
  };
}

export function getExtraUpsellGroup(option) {
  if (!option) return null;
  const extraUpsellGroupData =
    option?.extra_upsell_group ?? option?.extraUpsellGroup ?? option?.extras;
  if (!extraUpsellGroupData) return null;
  const upsellGroupList = Array.isArray(extraUpsellGroupData)
    ? extraUpsellGroupData
    : [extraUpsellGroupData];
  return upsellGroupList.filter(Boolean);
}

export function normalizeUpsellItem(item, index, group = {}) {
  const id = normalizeId(item?.upsell_item_id ?? item?.id ?? item?.modifier_id, `upsell-${index}`);
  const groupId =
    group?.upsell_group_id ??
    group?.upselling_group_id ??
    group?.group_id ??
    group?.id ??
    item?.upsell_group_id ??
    item?.upsell_group_ids?.[0] ??
    '';

  return {
    ...item,
    id,
    name:
      item?.display_name || item?.upsell_name || item?.name || item?.title || `Item ${index + 1}`,
    price:
      firstNumber(item?.upsell_price, item?.price, item?.price_delta, item?.selling_price, 0) ?? 0,
    upsell_group_id: groupId,
    upsell_group_name:
      group?.group_name ?? group?.name ?? group?.label ?? item?.upsell_group_names?.[0] ?? '',
    upsell_item_id: item?.upsell_item_id ?? id,
    modifier_id: item?.modifier_id ?? item?.upsell_item_id ?? item?.id ?? id,
  };
}

export function normalizeUpsellGroup(group, index) {
  const id = normalizeId(
    group?.upsell_group_id ?? group?.upselling_group_id ?? group?.group_id ?? group?.id,
    `upsell-group-${index}`,
  );
  const rawItems = asArray(group?.upsell_items ?? group?.items ?? group?.options);
  return {
    ...group,
    id,
    name:
      group?.group_name ??
      group?.name ??
      group?.label ??
      group?.display_name ??
      `Extras ${index + 1}`,
    items: rawItems.map((item, itemIndex) =>
      normalizeUpsellItem(item, itemIndex, { ...group, id }),
    ),
  };
}

export function getModifierCustomGroups(option) {
  const alternateModifiers = asArray(option?.alternate_modifiers ?? option?.alternateModifiers).map(
    normalizeModifier,
  );
  const extraGroups = getExtraUpsellGroup(option)?.map(normalizeUpsellGroup) ?? [];
  return { alternateModifiers, extraGroups };
}

export function hasModifierCustomOptions(option) {
  const custom = getModifierCustomGroups(option);
  return (
    custom.alternateModifiers.length > 0 ||
    custom.extraGroups.some((group) => group.items.length > 0)
  );
}

export function normalizeModifier(option, index, opts = {}) {
  const minQuantity =
    firstNumber(
      option?.min_quantity,
      option?.min_qty,
      option?.minQuantity,
      option?.minimum_quantity,
      0,
    ) ?? 0;
  const maxQuantity =
    firstNumber(
      option?.max_quantity,
      option?.max_qty,
      option?.maxQuantity,
      option?.maximum_quantity,
      0,
    ) ?? 0;
  const resolvedPriceDelta = resolveModifierOptionPrice(option, opts);

  return {
    ...option,
    id: normalizeId(option?.modifier_id ?? option?.id ?? option?.value, `modifier-${index}`),
    name: normalizeLabel(option, `Modifier ${index + 1}`),
    description: option?.description ?? option?.subtitle ?? option?.subtext ?? '',
    priceDelta: resolvedPriceDelta,
    minQuantity,
    maxQuantity,
  };
}

export function normalizeModifierGroup(group, index, opts = {}) {
  const options = asArray(group?.modifiers ?? group?.items ?? group?.options ?? group).map(
    (option, optionIndex) => normalizeModifier(option, optionIndex, opts),
  );
  const minSelection = firstNumber(group?.min_selection, group?.minSelection, 0) ?? 0;
  const maxSelection = firstNumber(group?.max_selection, group?.maxSelection, 0) ?? 0;
  const minModifier = firstNumber(group?.min_modifier, group?.minModifier, 0) ?? 0;
  const maxModifier = firstNumber(group?.max_modifier, group?.maxModifier, 0) ?? 0;
  const required = Boolean(group?.is_required ?? group?.required ?? minSelection > 0);
  return {
    id: normalizeId(
      group?.modifier_group_id ?? group?.group_id ?? group?.id,
      `modifier-group-${index}`,
    ),
    name: group?.name ?? group?.label ?? `Modifier Group ${index + 1}`,
    selectionType: String(group?.selection_type ?? group?.selectionType ?? 'MULTI').toUpperCase(),
    minSelection,
    maxSelection,
    minModifier,
    maxModifier,
    required,
    allowQuantity: Boolean(
      (group?.allow_quantity ?? group?.allowQuantity) ||
      minModifier > 0 ||
      maxModifier > 0 ||
      options.some((option) => option.minQuantity > 0 || option.maxQuantity > 0),
    ),
    options,
  };
}

export function normalizeIngredient(option, index) {
  return {
    id: normalizeId(
      option?.ingredient_id ?? option?.id ?? option?.value,
      option?.ingredient_name ?? option?.name ?? `ingredient-${index}`,
    ),
    name: normalizeLabel(option, `Ingredient ${index + 1}`),
    // Only ingredients explicitly marked excludable are removable; a missing
    // flag defaults to "not excludable" (this differs from the account-level
    // display flag, which defaults to shown when unset) since most recipe
    // lines are structural, not optional garnishes.
    isExcludable:
      option?.is_excludable === true ||
      option?.is_excludable === 'true' ||
      String(option?.is_excludable).toLowerCase() === 'true',
  };
}

// A combo position's/alternative's own nested variant + modifier configuration.
// Mirrors the top-level variantGroups/modifierGroups derivation (single-list
// synthesis included) but scoped to one nested `item_details`-shaped object.
export function normalizeComboDetail(detail, t) {
  const source = detail || {};
  const variantGroups = asArray(
    source.variant_group_details?.length ? source.variant_group_details : source.variant_groups,
  )
    .map(normalizeVariantGroup)
    .filter((group) => group.options.length > 0);
  const singleVariants = asArray(
    source.single_variant_details?.length
      ? source.single_variant_details
      : (source.single_variants ?? source.variants),
  ).map(normalizeVariant);
  if (singleVariants.length) {
    variantGroups.push({
      id: 'single-variants',
      name: t ? t('variants', 'Variants') : 'Variants',
      required: false,
      options: singleVariants,
    });
  }

  const modifierGroups = asArray(source.modifier_groups).map(normalizeModifierGroup);
  const singleModifiers = asArray(
    source.single_modifier_details?.length
      ? source.single_modifier_details
      : source.single_modifiers,
  ).map(normalizeModifier);
  if (singleModifiers.length) {
    modifierGroups.push({
      id: 'single-modifiers',
      name: t ? t('modifiers', 'Modifiers') : 'Modifiers',
      selectionType: 'MULTI',
      minSelection: 0,
      maxSelection: 0,
      minModifier: 0,
      maxModifier: 0,
      allowQuantity: singleModifiers.some((o) => o.minQuantity > 0 || o.maxQuantity > 0),
      options: singleModifiers,
      isSingleList: true,
    });
  }

  return {
    price:
      firstNumber(source.price, source.selling_price, source.online_price, source.amount, 0) ?? 0,
    uom: source.uom_details?.name ?? source.uom,
    variantGroups,
    modifierGroups,
    recipe: Array.isArray(source.recipe)
      ? source.recipe
      : Array.isArray(source.ingredients)
        ? source.ingredients
        : [],
  };
}

// Expands one combo alternative into one chip per variant option (or a single
// chip when the alternative has no variants of its own) — an alternative with
// sizes never gets a nested size picker under a single chip, per spec.
export function buildComboAlternativeChips(positionId, alternative, index, t) {
  const detail = normalizeComboDetail(alternative?.item_details ?? alternative, t);
  const altItemId = normalizeId(
    alternative?.item_id ?? alternative?.id,
    `${positionId}-alt-${index}`,
  );
  const altName = normalizeLabel(alternative, `Alternative ${index + 1}`);
  const variantOptions = detail.variantGroups.flatMap((group) =>
    group.options.map((option) => ({ ...option, groupId: group.id, groupName: group.name })),
  );

  if (variantOptions.length === 0) {
    return [
      {
        key: `${positionId}::${altItemId}`,
        positionId,
        alternativeItemId: altItemId,
        name: altName,
        variantId: null,
        variantGroupId: null,
        variantName: null,
        priceDelta: detail.price,
        detail,
      },
    ];
  }

  return variantOptions.map((variant) => ({
    key: `${positionId}::${altItemId}::${variant.id}`,
    positionId,
    alternativeItemId: altItemId,
    name: `${altName} (${variant.name})`,
    variantId: variant.id,
    variantGroupId: variant.groupId === 'single-variants' ? null : variant.groupId,
    variantName: variant.name,
    priceDelta: detail.price + (variant.priceDelta || 0),
    detail,
  }));
}

export function normalizeComboPosition(position, index, t) {
  const id = normalizeId(position?.position_id ?? position?.id, `position-${index}`);
  const detail = normalizeComboDetail(position?.item_details ?? position, t);
  const chips = asArray(position?.alternative_items ?? position?.alternatives).flatMap(
    (alt, altIndex) => buildComboAlternativeChips(id, alt, altIndex, t),
  );

  return {
    id,
    itemId: normalizeId(position?.item_id ?? position?.id, id),
    name: normalizeLabel(position, `Item ${index + 1}`),
    isDefault: Boolean(position?.is_default ?? position?.default),
    // Position's own per-item pick cap for multi-select groups; 0/undefined = uncapped
    // by the position itself (only the group's max_select applies).
    ownMaxQuantity: firstNumber(position?.quantity, 0) ?? 0,
    price: detail.price,
    detail,
    chips,
  };
}

export function normalizeComboGroup(group, index, t) {
  const positions = asArray(group?.combo_items ?? group?.positions ?? group?.items).map(
    (position, i) => normalizeComboPosition(position, i, t),
  );
  const maxSelect = firstNumber(group?.max_select, group?.maxSelect, 0) ?? 0;
  const minSelect = firstNumber(group?.min_select, group?.minSelect, 0) ?? 0;

  return {
    id: normalizeId(
      group?.combo_id ?? group?.combo_group_id ?? group?.group_id ?? group?.id,
      `combo-group-${index}`,
    ),
    name: group?.name ?? group?.label ?? `Combo Group ${index + 1}`,
    minSelect,
    maxSelect,
    isMultiSelect: maxSelect > 1,
    required: minSelect > 0,
    positions,
  };
}

// Pseudo variant group that scopes the selected SKU's service items inside
// serviceItemSelections (variant-SKU mode only).
export const SKU_SERVICE_GROUP = { id: 'variant-sku', variant_group_id: 'variant-sku' };

// Normalised modifier groups for a source that carries modifier_groups +
// single_modifiers: the product itself, or (sellable_level === 'variant') the
// selected SKU. Ungrouped modifiers become the synthetic 'single-modifiers' group.
export function buildModifierGroupList(source, opts, t) {
  const groups = asArray(source?.modifier_groups).map((group, index) =>
    normalizeModifierGroup(group, index, opts),
  );
  const singles = asArray(
    source?.single_modifier_details?.length
      ? source.single_modifier_details
      : source?.single_modifiers,
  ).map((modifier, index) => normalizeModifier(modifier, index, opts));
  if (singles.length) {
    groups.push({
      id: 'single-modifiers',
      name: t('modifiers', 'Modifiers'),
      selectionType: 'MULTI',
      minSelection: 0,
      maxSelection: 0,
      minModifier: 0,
      maxModifier: 0,
      allowQuantity: singles.some((option) => option.minQuantity > 0 || option.maxQuantity > 0),
      options: singles,
      isSingleList: true,
    });
  }
  return groups;
}

// The source of a variant-sellable item's modifiers: the SKU when it carries
// its own, else the item.
export function getModifierSource(product, sku) {
  return sku?.hasOwnModifiers
    ? { modifier_groups: sku.modifierGroups, single_modifiers: sku.singleModifiers }
    : product;
}

// One-line rule summary shown under a modifier group's title.
export function modifierSubtitle(group, t) {
  if (group.selectionType === 'SINGLE' && !group.allowQuantity) {
    return t('pleaseSelectOne', 'Please select any 1 option');
  }

  if (group.minSelection > 0 || group.maxSelection > 0) {
    const min = group.minSelection || 0;
    const max = group.maxSelection || 0;
    if (min > 0 && max > 0) {
      return `${t('select', 'Select')} ${min} ${t('toWord', 'to')} ${max} ${t('options', 'options')}`;
    }
    if (max > 0) {
      return `${t('selectUpTo', 'Select up to')} ${max} ${t('options', 'options')}`;
    }
    return `${t('selectAtLeast', 'Select at least')} ${min} ${t('options', 'options')}`;
  }

  const minQty = group.minModifier || 0;
  const maxQty = group.maxModifier || 0;
  if (minQty > 0 && maxQty > 0) {
    return `${t('select', 'Select')} ${minQty} ${t('toWord', 'to')} ${maxQty} ${t('qty', 'qty')}`;
  }
  if (maxQty > 0) {
    return `${t('selectUpTo', 'Select up to')} ${maxQty} ${t('qty', 'qty')}`;
  }
  if (minQty > 0) {
    return `${t('selectAtLeast', 'Select at least')} ${minQty} ${t('qty', 'qty')}`;
  }
  return t('optionalLabel', 'Optional');
}
